export type VetPromptModule = "creator-fit" | "script-vet" | "draft-vet";

type PromptGuide = {
  summary: string;
  bullets: string[];
  exampleOutline: string;
};

export const PROMPT_GUIDES: Record<VetPromptModule, PromptGuide> = {
  "creator-fit": {
    summary:
      "Your master prompt tells the AI how to judge whether an influencer fits this campaign. Be explicit about must-haves vs nice-to-haves.",
    bullets: [
      "State campaign goal, audience, and geography you care about.",
      "List mandatory criteria (niche, format, language, brand safety).",
      "Define how to score or rank (e.g. 0–100) and what each band means.",
      "Say what to output: verdict, gaps, and one-line rationale.",
      "Avoid naming specific creators — this prompt is reused for every check.",
    ],
    exampleOutline: `Role: Campaign fit reviewer for [campaign].

Evaluate each creator profile against:
1. Audience match (age, geo, interests)
2. Content niche & format alignment
3. Brand safety & tone
4. Past performance signals (if available)

Output: fit score, Shortlist / Consider / Pass, 2–3 gap bullets.`,
  },
  "script-vet": {
    summary:
      "Your master prompt is the rubric for vetting scripts. It should reference campaign mandatories, tone, and what “pass” looks like scene-by-scene.",
    bullets: [
      "Include non-negotiables: claims, disclaimers, hashtags, CTA, duration.",
      "Describe tone (creator-native vs brand) and what drift looks like.",
      "List checks: brief alignment, safety, legal, competitor mentions.",
      "Specify output format: pass/fail per section, severity, suggested fixes.",
      "Do not paste a full script here — this prompt applies to any script upload.",
    ],
    exampleOutline: `Role: Script reviewer for [campaign].

Check each scene for:
- Brief & creator brief alignment
- Mandatories (claims, #ad, product shots)
- Brand voice & banned topics
- Pacing vs agreed format (e.g. 30s Reel)

Output: overall verdict, flagged lines with severity, fix suggestions.`,
  },
  "draft-vet": {
    summary:
      "Your master prompt defines how to review uploaded draft video or stills against brief, approved script, and brand guidelines.",
    bullets: [
      "Call out visual mandatories: logo, product, packaging, on-screen text.",
      "Define script drift rules (what counts as a fail vs minor note).",
      "Include disclosure / #ad placement requirements.",
      "Note quality bar: lighting, framing, audio, platform-safe crops.",
      "Ask for timestamped findings when reviewing video.",
    ],
    exampleOutline: `Role: Draft QC for [campaign].

Compare draft to approved script and brand guidelines:
- Visual brand rules & product visibility
- Script adherence (dialogue, supers, order of beats)
- Disclosures & regulated claims on screen
- Technical quality for [platform]

Output: pass / revise, timestamped issues, priority fixes.`,
  },
};
